import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { APIKey, IAPIKey, TAPIKeyScope } from '@feathr/blackbox';
import { APIKeyServiceNames } from '@feathr/blackbox';
import { showError, submitModelWithErrorHandling } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { selectedScopesToArray } from './ScopePicker/ScopePicker.utils';

export interface IUseCreateAPIKeyOptions {
  onServiceKeyCreated: (secret: string) => void;
  /** Expiry for the default preset, so leaving the default still sends expires_at. */
  initialExpiresAt?: string;
}

export interface IUseCreateAPIKey {
  serviceName: APIKeyServiceNames;
  name: string;
  selectedScopes: Set<TAPIKeyScope>;
  canCreate: boolean;
  isCreating: boolean;
  setName: (name: string) => void;
  setScopes: (scopes: Set<TAPIKeyScope>) => void;
  setExpiresAt: (expiresAt: string | undefined) => void;
  setIsExpirationComplete: (isComplete: boolean) => void;
  handleCreate: () => Promise<void>;
}

/** Create-form state + create wiring for a V2 service key (Feathr API only; Zapier deferred to #8553). */
export function useCreateAPIKey({
  onServiceKeyCreated,
  initialExpiresAt,
}: IUseCreateAPIKeyOptions): IUseCreateAPIKey {
  const { t } = useTranslation();
  const { APIKeys } = useStore();

  const serviceName = APIKeyServiceNames.FeathrAPI;
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<Set<TAPIKeyScope>>(new Set<TAPIKeyScope>());
  const [expiresAt, setExpiresAt] = useState<string | undefined>(initialExpiresAt);
  const [isCreating, setIsCreating] = useState(false);
  // False for "Custom date" until a date is picked, so create can't fire a never-expiring key.
  const [isExpirationComplete, setIsExpirationComplete] = useState(true);

  const canCreate = name.trim().length > 0 && selectedScopes.size > 0 && isExpirationComplete;

  async function handleCreate(): Promise<void> {
    if (!canCreate) {
      return;
    }

    // `service_name` is the user's key name; the service maps to `kind`. Cast
    // because blackbox types service_name as the narrower enum (widen in #8547).
    const attributes: Partial<IAPIKey> = {
      kind: 'service',
      scopes: selectedScopesToArray(selectedScopes),
      service_name: name.trim() as APIKeyServiceNames,
    };
    if (expiresAt !== undefined) {
      attributes.expires_at = expiresAt;
    }

    const model = APIKeys.create(attributes);

    setIsCreating(true);
    await submitModelWithErrorHandling<APIKey>({
      onSuccess: (created) => {
        const secret = created.get('secret');
        if (secret) {
          onServiceKeyCreated(secret);
        } else {
          // Secret is returned only on create; a 200 without it can't be recovered.
          showError(
            new Error(t('The key was created but its secret was not returned. Please try again.')),
            t,
          );
        }
      },
      operation: APIKeys.add(model),
      t,
    });
    setIsCreating(false);
  }

  return {
    canCreate,
    handleCreate,
    isCreating,
    name,
    selectedScopes,
    serviceName,
    setExpiresAt,
    setIsExpirationComplete,
    setName,
    setScopes: setSelectedScopes,
  };
}
